/**
 * Amount formatter with locale-based thousand separators and M/K shortcuts
 */

/**
 * Parse a numeric string, auto-detecting locale format.
 * Handles both EN (1,234.56) and ES/PT (1.234,56) formats.
 * When `locale` is provided, resolves the ambiguous case where exactly
 * 3 digits follow a comma (e.g. "9,903"): ES/PT → 9.903, EN → 9903.
 */
const parseNumericValue = (str: string, locale?: string): number | null => {
  // Normalize Unicode minus signs and en-dashes to ASCII hyphen-minus
  // Excel and email clients often use these instead of '-'
  str = str.replace(/[\u2212\u2013\u2014\u00AD]/g, '-');
  // Handle accounting-style negatives: (123.45) → -123.45
  const accountingMatch = str.match(/^\((.+)\)$/);
  if (accountingMatch) str = '-' + accountingMatch[1];

  const lastCommaPos = str.lastIndexOf(',');
  const lastDotPos = str.lastIndexOf('.');

  let normalized: string;

  if (lastCommaPos > lastDotPos) {
    // Comma appears after last dot — comma is likely the decimal separator
    // e.g. "1.234,56" or "0,02"
    const digitsAfterComma = str.length - lastCommaPos - 1;
    if (digitsAfterComma === 3 && lastDotPos === -1) {
      // Ambiguous: "9,903" could be EN thousands (9903) or ES/PT decimal (9.903).
      // When locale is known, use it to resolve; otherwise default to EN thousands.
      const useCommaDecimal = locale === 'es' || locale === 'pt';
      if (useCommaDecimal) {
        normalized = str.replace(',', '.');
      } else {
        normalized = str.replace(/,/g, '');
      }
    } else {
      // ES/PT decimal format (e.g. "0,02", "1.234,56")
      normalized = str.replace(/\./g, '').replace(',', '.');
    }
  } else if (lastDotPos !== -1 && lastCommaPos === -1) {
    // Only dots, no comma.  Apply the same 3-digit heuristic we use for
    // commas: if every dot-separated segment (after the first) has exactly
    // 3 digits, treat dots as thousands separators (ES/PT format).
    // e.g. "8.631" → 8631, "1.234.567" → 1234567
    // Otherwise treat the last dot as a decimal point (EN format).
    // e.g. "0.02" → 0.02, "924.87" → 924.87, "1.2340" → 1.234
    const absStr = str.replace(/^-/, '');
    const dotParts = absStr.split('.');
    const allThousands = dotParts.length > 1 && dotParts.slice(1).every(p => p.length === 3);
    if (allThousands) {
      normalized = str.replace(/\./g, '');
    } else {
      normalized = str;  // keep dot as decimal
    }
  } else {
    // Dot appears after last comma — standard EN format
    // e.g. "1,234.56"
    normalized = str.replace(/,/g, '');
  }

  // Reject if normalized string contains non-numeric characters
  // (digits, optional leading minus, at most one decimal point)
  if (!/^-?\d*\.?\d+$/.test(normalized)) return null;

  const num = parseFloat(normalized);
  return isNaN(num) ? null : num;
};

/**
 * Parse amount input with M/K shortcuts
 * Examples: "1M" -> 1000000, "2.5M" -> 2500000, "500K" -> 500000
 * Also handles both EN (dot decimal) and ES/PT (comma decimal) formats.
 * @param input - String input from user
 * @param locale - Optional locale ('es','pt','en') to resolve ambiguous cases like "9,903"
 * @returns Parsed number or null if invalid
 */
export const parseAmountInput = (input: string, locale?: string): number | null => {
  if (!input || input.trim() === '') return null;

  const trimmed = input.trim().toUpperCase();

  // Check for M (millions) or K (thousands) suffix
  const hasM = trimmed.endsWith('M');
  const hasK = trimmed.endsWith('K');

  if (hasM || hasK) {
    const num = parseNumericValue(trimmed.slice(0, -1), locale);
    if (num === null) return null;
    return hasM ? num * 1000000 : num * 1000;
  }

  return parseNumericValue(trimmed, locale);
};

/**
 * Format number with locale-based thousand separators
 * ES/PT: 1.234.567,89 (dot for thousands, comma for decimal)
 * EN: 1,234,567.89 (comma for thousands, dot for decimal)
 * @param value - Number to format
 * @param locale - Locale string (en, es, pt)
 * @param decimals - Number of decimal places
 * @returns Formatted string
 */
export const formatAmountDisplay = (
  value: number | null,
  locale: string,
  decimals: number = 2
): string => {
  if (value === null || isNaN(value)) return '';

  // Determine separators based on locale
  const useCommaForDecimal = locale === 'es' || locale === 'pt';

  if (useCommaForDecimal) {
    // ES/PT format: 1.234.567,89
    const parts = value.toFixed(decimals).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return parts[1] ? `${integerPart},${parts[1]}` : integerPart;
  } else {
    // EN format: 1,234,567.89
    const parts = value.toFixed(decimals).split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts[1] ? `${integerPart}.${parts[1]}` : integerPart;
  }
};

/**
 * Format amount for input field (no thousand separators during editing)
 * @param value - Number to format
 * @param locale - Locale string
 * @param decimals - Number of decimal places
 * @returns Formatted string for input
 */
export const formatAmountForInput = (
  value: number | null,
  locale: string,
  decimals: number = 2
): string => {
  if (value === null || isNaN(value)) return '';

  const useCommaForDecimal = locale === 'es' || locale === 'pt';

  if (useCommaForDecimal) {
    return value.toFixed(decimals).replace('.', ',');
  } else {
    return value.toFixed(decimals);
  }
};

/**
 * Format price with smart decimal places (removes trailing zeros)
 * Examples: 932.12 -> "932,12", 932.1200 -> "932,12", 932 -> "932"
 * @param value - Number to format
 * @param locale - Locale string
 * @param maxDecimals - Maximum number of decimal places (default 4)
 * @returns Formatted string with only necessary decimals
 */
export const formatPriceDisplay = (
  value: number | null,
  locale: string,
  maxDecimals: number = 4
): string => {
  if (value === null || isNaN(value)) return '';

  // Determine separators based on locale
  const useCommaForDecimal = locale === 'es' || locale === 'pt';

  // Convert to string with max decimals, then remove trailing zeros
  const fixedStr = value.toFixed(maxDecimals);
  const [intPart, decPart] = fixedStr.split('.');

  // Remove trailing zeros from decimal part
  const trimmedDec = decPart.replace(/0+$/, '');

  if (useCommaForDecimal) {
    // ES/PT format: 1.234.567,89
    const integerPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return trimmedDec ? `${integerPart},${trimmedDec}` : integerPart;
  } else {
    // EN format: 1,234,567.89
    const integerPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return trimmedDec ? `${integerPart}.${trimmedDec}` : integerPart;
  }
};
